import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BDK, waUrl } from "../../config/bdk";

const NAV_LINKS = [
  { label: "Inicio", href: "#inicio" },
  { label: "Precios", href: "#tipos-paginas" },
  { label: "Proyectos", href: "#portfolio" },
  { label: "Cómo funciona", href: "#proceso" },
  { label: "Preguntas frecuentes", href: "#faq" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("inicio");

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Track which section is in view for active nav highlight
  useEffect(() => {
    const ids = ["inicio", ...NAV_LINKS.map((l) => l.href.replace("#", ""))];
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!targets.length || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <header
      data-testid="landing-navbar"
      className="fixed top-0 inset-x-0 z-50"
      style={{
        background: "rgba(17,18,24,.94)",
        borderBottom: "1px solid rgba(217,0,32,.25)",
        boxShadow: "0 10px 35px rgba(0,0,0,.18)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div className="container-bdk flex items-center gap-7 h-[72px]">
        <a
          href="#inicio"
          data-testid="navbar-logo"
          className="block w-[190px] md:w-[210px] flex-shrink-0"
        >
          <img
            src={BDK.logos.redWhite}
            alt="BDKSISTEMAS"
            className="w-full h-auto object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-6 ml-auto text-white/80 text-[13px] font-bold">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className={`nav-link uppercase tracking-wide transition-colors ${
                activeId === l.href.replace("#", "")
                  ? "text-[#d90020]"
                  : "hover:text-[#d90020]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 ml-2">
          <Link
            to="/otros-servicios"
            data-testid="navbar-otros-servicios"
            onClick={() => window.fbq?.("trackCustom", "ClickOtrosServicios")}
            className="text-white/45 hover:text-white/75 text-[11px] font-semibold uppercase tracking-wide transition-colors"
          >
            Otros servicios
          </Link>
          <a
            href="#contacto"
            data-testid="navbar-cta-formulario"
            className="bdk-btn bdk-btn--primary"
          >
            Solicitar propuesta
          </a>
        </div>

        <button
          data-testid="navbar-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden ml-auto w-11 h-11 rounded-md flex flex-col items-center justify-center gap-[5px]"
          style={{
            border: "1px solid rgba(255,255,255,.12)",
            background: "rgba(255,255,255,.04)",
          }}
          aria-label="Menu"
        >
          {open ? (
            <X size={22} className="text-white" />
          ) : (
            <>
              <span className="block w-6 h-[2px] bg-white" />
              <span className="block w-6 h-[2px] bg-white" />
              <span className="block w-6 h-[2px] bg-white" />
            </>
          )}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden absolute top-[72px] inset-x-0 flex flex-col gap-2 px-5 py-6"
          style={{
            background: "rgba(17,18,24,.98)",
            borderBottom: "1px solid rgba(217,0,32,.25)",
            boxShadow: "0 20px 35px rgba(0,0,0,.25)",
          }}
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className={`py-2.5 text-[13px] font-bold uppercase tracking-wide ${
                activeId === l.href.replace("#", "")
                  ? "text-[#d90020]"
                  : "text-white/85 hover:text-[#d90020]"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/otros-servicios"
            onClick={() => {
              window.fbq?.("trackCustom", "ClickOtrosServicios");
              setOpen(false);
            }}
            data-testid="navbar-mobile-otros-servicios"
            className="py-2.5 text-[12px] font-semibold uppercase tracking-wide text-white/45"
          >
            Otros servicios
          </Link>
          <div className="flex flex-col gap-2 mt-3">
            <a
              href="#contacto"
              onClick={() => setOpen(false)}
              className="bdk-btn bdk-btn--primary bdk-btn--lg w-full"
            >
              Solicitar propuesta
            </a>
            <a
              href={waUrl()}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="bdk-btn bdk-btn--whatsapp bdk-btn--lg w-full"
            >
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
